/**
 * How often the controller reports a playing score's position, in milliseconds, per transport.
 *
 * Direct links get a fixed 250 ms ticker that runs only while playing
 * (`internal/playback/provider.go`, `playheadInterval = 250 * time.Millisecond`). The cloud mirror is
 * far coarser: position is deliberately excluded from the mirror's change fingerprint, so a playing
 * score that is otherwise unchanged is republished on the slow playhead tick alone
 * (`internal/cloud/sync/mirror_publisher.go`, `mirrorTickInterval = 2 * time.Second`) — one report
 * every two seconds.
 *
 * Unlike the output preview, the direct cadence is measured to be exactly what it claims: 79 gaps
 * against a live daemon came in at a mean of 250.0 ms with a standard deviation of 0.6 ms, and every
 * reported position step fell between 249.1 and 250.9 ms. So the bar steps a clean quarter second at a
 * time on LAN — and a clean two seconds at a time through the cloud — which is precisely the problem.
 */
export const PLAYHEAD_NOMINAL_INTERVAL_MS = { lan: 250, cloud: 2_000 } as const;

/** A report this far from the prediction is a seek or a restart, not drift — take it whole. */
const HARD_RESYNC_SECONDS = .5;
/**
 * Ceiling on how much a soft correction may bend the displayed speed. A quarter keeps the bar inside
 * 0.75x–1.25x, which is imperceptible and, more importantly, always still forwards.
 */
export const PLAYHEAD_MAX_SLEW_RATE = .25;

export interface PlayheadReport {
  playing: boolean;
  positionSeconds: number | undefined;
  durationSeconds: number;
  loop: boolean;
  /**
   * The controller's own count of completed passes, from 1.7.24 onward. Present makes a wrap a FACT
   * rather than a guess: the count went up, so the score restarted. Absent keeps the heuristic.
   */
  loopCount?: number;
  /**
   * When the controller took this snapshot, on its own wall clock in ms. It is never compared with this
   * machine's clock — the two disagree and no correction here could fix that — and is used for one
   * thing only: telling one snapshot from another. Absent means an older daemon, which publishes no
   * such stamp in any state.
   */
  snapshotAt?: number;
}

function shortestOnCircle(delta: number, span: number): number {
  const half = span / 2;
  return ((delta + half) % span + span) % span - half;
}

/**
 * A local playhead clock — why the progress bar sweeps instead of stepping.
 *
 * The controller reports a playing score's position on a timer, not continuously: four times a second
 * over a direct link, and only once every two seconds through the cloud mirror. Binding the bar
 * straight to that number makes it jump by a quarter second — or by two whole seconds — and then sit
 * still. But the position it reports is honest and dull: it comes off a monotonic clock and advances at
 * exactly wall-clock rate, with no timecode, rate scaling or drift correction anywhere in that path. So
 * between reports we can simply keep counting, and use each new report to check our count.
 *
 * A small disagreement — a few milliseconds of clock drift — is absorbed by bending the displayed speed
 * slightly for one report interval, never by jumping; {@link PLAYHEAD_MAX_SLEW_RATE} keeps that bend
 * small enough that the bar always still moves forward. A large one is not drift but a seek, a restart,
 * or a loop wrap, and is taken whole.
 *
 * A looping score's wrap is predicted locally, and from 1.7.24 the controller also publishes a snapshot
 * at the wrap and counts the passes, so the prediction is CHECKED rather than merely trusted. Before
 * that count existed a wrap had to be guessed from the position coming back smaller, which cannot be
 * told from a deliberate rewind to the top and is wrong outright whenever a report gap exceeds half the
 * score. Both paths are live, because a room is whatever daemon it is running.
 *
 * While paused or stopped nothing is advancing, so the clock stops and the reported position is shown
 * exactly. While the room says PLAYING the clock keeps counting: the position of record is the room's,
 * and between the room's pushes the only honest thing a bar can do is carry the last one forward at the
 * rate a score advances. It stops when the room stops saying it is playing, and not on a timer of the
 * console's own — a score that loops every ten seconds through a cloud that pushes every two is
 * indistinguishable, from in here, from one that pushes only at the wrap.
 */
export class PlayheadClock {
  private basePosition = 0;
  private baseAt = 0;
  private residual = 0;
  private residualAt = 0;
  private residualWindowMs = 1;
  private lastReported: number | undefined;
  private lastSnapshotAt: number | undefined;
  private lastLoopCount: number | undefined;
  private duration = 0;
  private loop = false;
  private running = false;
  private intervalMs: number;

  constructor(nominalIntervalMs: number) {
    this.intervalMs = nominalIntervalMs;
  }

  /** True while the clock should be advanced on every animation frame. */
  get advancing(): boolean {
    return this.running;
  }

  setNominalInterval(nominalIntervalMs: number): void {
    this.intervalMs = nominalIntervalMs;
  }

  private bounded(value: number): number {
    if (this.duration <= 0) return Math.max(0, value);
    if (!this.loop) return Math.min(this.duration, Math.max(0, value));
    return ((value % this.duration) + this.duration) % this.duration;
  }

  /** The position to show at this instant, in seconds. */
  at(localNow: number): number {
    if (!this.running) return this.bounded(this.basePosition);
    const decay = this.residual === 0 ? 0 : Math.max(0, 1 - (localNow - this.residualAt) / this.residualWindowMs);
    return this.bounded(this.basePosition + (localNow - this.baseAt) / 1_000 + this.residual * decay);
  }

  /**
   * Fold in what the controller just said. Returns true when this was a fresh sample worth acting on —
   * a republished snapshot carrying a position we have already seen is ignored, because re-anchoring on
   * a stale number would drag the bar backwards.
   */
  report(report: PlayheadReport, localNow: number): boolean {
    this.duration = report.durationSeconds > 0 ? report.durationSeconds : 0;
    this.loop = report.loop;
    const value = report.positionSeconds;

    if (!report.playing || !Number.isFinite(value)) {
      this.running = false;
      this.basePosition = Number.isFinite(value) ? value as number : 0;
      this.baseAt = localNow;
      this.residual = 0;
      this.lastReported = undefined;
      this.lastSnapshotAt = report.snapshotAt;
      this.lastLoopCount = report.snapshotAt != null ? report.loopCount ?? 0 : undefined;
      return true;
    }

    const position = value as number;
    /**
     * Is this the same report again?
     *
     * A controller that stamps its snapshots answers that itself, and it must be asked rather than the
     * POSITION compared — because a looping score republishes at position ≈ 0 on every single wrap, so
     * a console deduping on position throws away every wrap after the first and its bar stops at the
     * end of the first pass, forever. Without a stamp the position is all there is, and a republished
     * snapshot carrying a position we have already acted on is still worth ignoring: re-anchoring on a
     * stale number drags the bar backwards.
     */
    const stamped = report.snapshotAt != null;
    const repeated = stamped ? report.snapshotAt === this.lastSnapshotAt : this.lastReported === position;
    if (this.running && repeated) return false;

    const wrapping = this.loop && this.duration > 0;
    const anchored = this.running && this.lastReported != null;
    const before = anchored ? this.at(localNow) : position;
    const drift = wrapping ? shortestOnCircle(position - before, this.duration) : position - before;
    /**
     * A wrap is a FACT when the controller counts passes: the count went up, the score restarted, and
     * there is nothing to infer. The half-duration guess it replaces is only ever a guess — it cannot
     * tell a wrap from a deliberate rewind to the top, and it is wrong outright for any report gap
     * longer than half the score, which is every cloud gap on a four-second cue.
     *
     * The count is read from the SNAPSHOT's presence, never from the count's own: a controller that
     * stamps its snapshots always counts, and it omits the count on the first pass because zero is its
     * default — so an absent `loopCount` beside a stamp means none completed, and is not evidence of an
     * older daemon. Any increase is a wrap, not only an increase of one: a tick delayed past a whole
     * pass of a short score crosses more than one, and the controller says how many rather than
     * pretending it was one.
     */
    const passes = stamped ? report.loopCount ?? 0 : undefined;
    const wrapped = passes != null && this.lastLoopCount != null
      ? passes > this.lastLoopCount
      : wrapping && anchored && (this.lastReported as number) - position > this.duration / 2;
    const rewound = anchored && position < (this.lastReported as number) - 1e-3 && !wrapped;

    this.running = true;
    this.basePosition = position;
    this.baseAt = localNow;
    this.residual = rewound || Math.abs(drift) > HARD_RESYNC_SECONDS ? 0 : -drift;
    this.residualAt = localNow;
    this.residualWindowMs = Math.max(this.intervalMs, Math.abs(this.residual) * 1_000 / PLAYHEAD_MAX_SLEW_RATE);
    this.lastReported = position;
    this.lastSnapshotAt = report.snapshotAt;
    this.lastLoopCount = passes;
    return true;
  }

}
